import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ContextStatement {
  id: string;
  text: string;
  status: "not-started" | "in-progress" | "completed" | "blocked" | "other";
  statusDetail?: string; // AI's flexible status text (e.g., "reference", "design decision")
  priority: "low" | "medium" | "high";
  createdAt: Date;
  completedAt?: Date;
  dueDate?: Date;
  dueDateType?: "hard" | "soft" | "contingent";
  orderIndex: number;
}

interface DbContextStatement {
  id: string;
  user_id: string;
  text: string;
  status: string;
  status_detail?: string | null; // Optional until migration is applied
  priority: string;
  due_date: string | null;
  due_date_type: string | null;
  order_index: number;
  created_at: string;
  completed_at: string | null;
  updated_at: string;
}

const dbToLocal = (db: DbContextStatement): ContextStatement => ({
  id: db.id,
  text: db.text,
  status: db.status as ContextStatement["status"],
  statusDetail: db.status_detail || undefined,
  priority: db.priority as ContextStatement["priority"],
  createdAt: new Date(db.created_at),
  completedAt: db.completed_at ? new Date(db.completed_at) : undefined,
  dueDate: db.due_date ? new Date(db.due_date) : undefined,
  dueDateType: db.due_date_type as ContextStatement["dueDateType"],
  orderIndex: db.order_index,
});

const localToDb = (
  local: Omit<ContextStatement, "id" | "createdAt">
): Omit<
  DbContextStatement,
  "id" | "user_id" | "created_at" | "updated_at"
> => ({
  text: local.text,
  status: local.status,
  status_detail: local.statusDetail || null,
  priority: local.priority,
  due_date: local.dueDate?.toISOString() || null,
  due_date_type: local.dueDateType || null,
  order_index: local.orderIndex,
  completed_at: local.completedAt?.toISOString() || null,
});

export const useContextStatements = () => {
  const [statements, setStatements] = useState<ContextStatement[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch statements from database
  const fetchStatements = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setStatements([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("context_statements")
        .select("*")
        .order("order_index", { ascending: true });

      if (error) {
        console.error("Error fetching context statements:", error);
        toast.error("Failed to load context statements");
        return;
      }

      if (data) {
        setStatements(data.map(dbToLocal));
      }
    } catch (error) {
      console.error("Error in fetchStatements:", error);
      toast.error("Failed to load context statements");
    } finally {
      setLoading(false);
    }
  };

  // Create new statement
  const createStatement = async (
    statement: Omit<ContextStatement, "id" | "createdAt">
  ) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in");
        return null;
      }

      const { data, error } = await supabase
        .from("context_statements")
        .insert([
          {
            user_id: user.id,
            ...localToDb(statement),
          },
        ])
        .select()
        .single();

      if (error) {
        console.error("Error creating statement:", error);
        toast.error("Failed to create statement");
        return null;
      }

      const newStatement = dbToLocal(data);
      setStatements((prev) => [...prev, newStatement]);
      return newStatement;
    } catch (error) {
      console.error("Error in createStatement:", error);
      toast.error("Failed to create statement");
      return null;
    }
  };

  // Update statement
  const updateStatement = async (
    id: string,
    updates: Partial<ContextStatement>
  ) => {
    try {
      const current = statements.find((s) => s.id === id);
      if (!current) return false;

      const merged: Omit<ContextStatement, "id" | "createdAt"> = {
        text: updates.text ?? current.text,
        status: updates.status ?? current.status,
        priority: updates.priority ?? current.priority,
        orderIndex: updates.orderIndex ?? current.orderIndex,
        completedAt:
          updates.completedAt !== undefined
            ? updates.completedAt
            : current.completedAt,
        dueDate:
          updates.dueDate !== undefined ? updates.dueDate : current.dueDate,
        dueDateType:
          updates.dueDateType !== undefined
            ? updates.dueDateType
            : current.dueDateType,
      };

      const { error } = await supabase
        .from("context_statements")
        .update(localToDb(merged))
        .eq("id", id);

      if (error) {
        console.error("Error updating statement:", error);
        toast.error("Failed to update statement");
        return false;
      }

      setStatements((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...updates } : s))
      );
      return true;
    } catch (error) {
      console.error("Error in updateStatement:", error);
      toast.error("Failed to update statement");
      return false;
    }
  };

  // Delete statement
  const deleteStatement = async (id: string) => {
    try {
      const { error } = await supabase
        .from("context_statements")
        .delete()
        .eq("id", id);

      if (error) {
        console.error("Error deleting statement:", error);
        toast.error("Failed to delete statement");
        return false;
      }

      setStatements((prev) => prev.filter((s) => s.id !== id));
      return true;
    } catch (error) {
      console.error("Error in deleteStatement:", error);
      toast.error("Failed to delete statement");
      return false;
    }
  };

  // Bulk update (for reordering, etc.)
  const bulkUpdate = async (
    updates: Array<{ id: string; updates: Partial<ContextStatement> }>
  ) => {
    try {
      const promises = updates.map(({ id, updates: statementUpdates }) => {
        const current = statements.find((s) => s.id === id);
        if (!current) return Promise.resolve({ error: null });

        const merged: Omit<ContextStatement, "id" | "createdAt"> = {
          text: statementUpdates.text ?? current.text,
          status: statementUpdates.status ?? current.status,
          priority: statementUpdates.priority ?? current.priority,
          orderIndex: statementUpdates.orderIndex ?? current.orderIndex,
          completedAt:
            statementUpdates.completedAt !== undefined
              ? statementUpdates.completedAt
              : current.completedAt,
          dueDate:
            statementUpdates.dueDate !== undefined
              ? statementUpdates.dueDate
              : current.dueDate,
          dueDateType:
            statementUpdates.dueDateType !== undefined
              ? statementUpdates.dueDateType
              : current.dueDateType,
        };

        return supabase
          .from("context_statements")
          .update(localToDb(merged))
          .eq("id", id);
      });

      const results = await Promise.all(promises);
      const hasError = results.some((r) => r && r.error);

      if (hasError) {
        console.error("Error in bulk update");
        toast.error("Failed to update some statements");
        return false;
      }

      // Update local state
      setStatements((prev) => {
        const newStatements = [...prev];
        updates.forEach(({ id, updates: statementUpdates }) => {
          const index = newStatements.findIndex((s) => s.id === id);
          if (index !== -1) {
            newStatements[index] = {
              ...newStatements[index],
              ...statementUpdates,
            };
          }
        });
        return newStatements;
      });

      return true;
    } catch (error) {
      console.error("Error in bulkUpdate:", error);
      toast.error("Failed to update statements");
      return false;
    }
  };

  // Sync entire state (for undo/redo or complete replacements)
  const syncStatements = async (newStatements: ContextStatement[]) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in");
        return false;
      }

      // If empty array, just delete all
      if (newStatements.length === 0) {
        const { error } = await supabase
          .from("context_statements")
          .delete()
          .neq("id", "00000000-0000-0000-0000-000000000000"); // Delete all

        if (error) {
          console.error("Error clearing statements:", error);
          toast.error("Failed to clear statements");
          return false;
        }
        return true;
      }

      // Delete all existing statements
      await supabase
        .from("context_statements")
        .delete()
        .neq("id", "00000000-0000-0000-0000-000000000000"); // Delete all

      // Insert new statements
      const { error } = await supabase.from("context_statements").insert(
        newStatements.map((s, index) => {
          const stmtWithIndex: Omit<ContextStatement, "id" | "createdAt"> = {
            text: s.text,
            status: s.status,
            priority: s.priority,
            orderIndex: index,
            completedAt: s.completedAt,
            dueDate: s.dueDate,
            dueDateType: s.dueDateType,
          };

          return {
            id: s.id,
            user_id: user.id,
            ...localToDb(stmtWithIndex),
          };
        })
      );

      if (error) {
        console.error("Error syncing statements:", error);
        toast.error("Failed to sync statements");
        return false;
      }

      setStatements(newStatements);
      return true;
    } catch (error) {
      console.error("Error in syncStatements:", error);
      toast.error("Failed to sync statements");
      return false;
    }
  };

  useEffect(() => {
    fetchStatements();

    // Subscribe to realtime changes
    const channel = supabase
      .channel("context_statements_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "context_statements",
        },
        () => {
          fetchStatements();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    statements,
    loading,
    createStatement,
    updateStatement,
    deleteStatement,
    bulkUpdate,
    syncStatements,
    refetch: fetchStatements,
  };
};
